import { applyKeyframe, analyzeQposStructure } from "../../core/keyframeHandler";
import type { BallJoint, Body, BodyUUID, HingeJoint, Joint, SlideJoint } from "../../types/scene";
import { buildBodyRootMap, findBodyForJointUuid } from "../../utils/bodyHierarchy";
import { getJointValuesFromMap } from "../../utils/jointValues";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { commitBodyTransform } from "../../viewer/utils/commitTransform";
import { getPhysicsWorker } from "../../physics/workerHandle";
import { logger } from "../../utils/logger";
import { getBodyTree, type BodyWithChildren } from "../sceneActions/bodyUtils";
import { getSceneActions, getSceneState } from "../sceneStore";
import { usePhysicsStore } from "../physicsStore";
import { useSelectionStore } from "../selectionStore";
import type {
  FileStoreActions,
  FileStoreGet,
  FileStoreSet,
  HandleKeyframeSelectOptions,
  KeyframeApplyResult,
  QposStructure,
} from "./types";

const isScalarJoint = (joint: Joint): joint is HingeJoint | SlideJoint =>
  joint.kind === "hinge" || joint.kind === "slide";
type BodyLookup = Record<string, Body | undefined>;
type WorldBodyEntry = string | { uuid?: string; name?: string };

/** Root free-joint pose: update scene store + physics worker so hinge FK matches simulation. */
const syncFreeJointRootPose = (
  sceneState: ReturnType<typeof getSceneState>,
  sceneActions: ReturnType<typeof getSceneActions>,
  hostBodyUuid: string,
  position: [number, number, number],
  orientation: [number, number, number, number]
) => {
  commitBodyTransform(sceneActions, hostBodyUuid, position, orientation);
  try {
    const physicsState = usePhysicsStore.getState();
    const worker = getPhysicsWorker();
    if (physicsState.isRunning && worker) {
      const bodyName =
        (sceneState.bodies as BodyLookup | undefined)?.[hostBodyUuid]?.name || hostBodyUuid;
      worker.postMessage({
        type: "setFreeJointPose",
        payload: { bodyName: String(bodyName), pose: [...position, ...orientation] },
      });
    }
  } catch {
    // Worker optional; visual still updates from commitBodyTransform
  }
};

const getBodyFreeJointQpos = (
  sceneState: ReturnType<typeof getSceneState>,
  joint: Joint
): number[] => {
  const hostBodyUuid = findBodyForJointUuid(sceneState.bodies || {}, joint.uuid);
  const body = hostBodyUuid ? (sceneState.bodies as BodyLookup | undefined)?.[hostBodyUuid] : null;
  const pos = Array.isArray(body?.position) ? body.position : [0, 0, 0];
  const quat = Array.isArray(body?.orientation) ? body.orientation : [1, 0, 0, 0];
  return [
    Number(pos[0]) || 0,
    Number(pos[1]) || 0,
    Number(pos[2]) || 0,
    Number(quat[0]) || 1,
    Number(quat[1]) || 0,
    Number(quat[2]) || 0,
    Number(quat[3]) || 0,
  ];
};

const getJointQposValues = (
  sceneState: ReturnType<typeof getSceneState>,
  joint: Joint,
  currentJointValues: Record<string, number>
): number[] => {
  if (joint.kind === "free") {
    return getBodyFreeJointQpos(sceneState, joint);
  }
  if (joint.kind === "ball") {
    const quat = (joint as BallJoint).quat;
    return Array.isArray(quat) && quat.length === 4
      ? [Number(quat[0]) || 1, Number(quat[1]) || 0, Number(quat[2]) || 0, Number(quat[3]) || 0]
      : [1, 0, 0, 0];
  }
  if (isScalarJoint(joint)) {
    return [currentJointValues[joint.name] ?? 0];
  }
  return [];
};

const getJointsFromBodyTreeInOrder = (
  bodyTree: BodyWithChildren[],
  jointsMap: Record<string, Joint>
): Joint[] => {
  const ordered: Joint[] = [];
  const seen = new Set<string>();

  const visit = (node: BodyWithChildren) => {
    if (!node) return;
    (node.joints || []).forEach((jointUuid: string) => {
      const key = String(jointUuid);
      if (seen.has(key)) return;
      const joint = jointsMap[key];
      if (!joint) return;
      ordered.push(joint);
      seen.add(key);
    });
    (node.children || []).forEach((child) => visit(child));
  };

  (bodyTree || []).forEach((root) => visit(root));

  // Keep behavior resilient if tree reconstruction is temporarily out of sync.
  Object.values(jointsMap || {}).forEach((joint) => {
    const key = String(joint.uuid);
    if (seen.has(key)) return;
    ordered.push(joint);
    seen.add(key);
  });

  return ordered;
};

const getScopedBodiesAndJoints = (
  sceneState: ReturnType<typeof getSceneState>,
  ownerWorldBodyUuid?: string
) => {
  const jointsMap = sceneState.joints || {};
  const bodiesMap = (sceneState.bodies || {}) as BodyLookup;
  const fullBodyTree = getBodyTree(sceneState);
  const orderedAllJoints = getJointsFromBodyTreeInOrder(fullBodyTree, jointsMap);
  if (ownerWorldBodyUuid && !bodiesMap[ownerWorldBodyUuid]) {
    logger.warn(
      `[KeyframeActions] Scoped keyframe owner "${ownerWorldBodyUuid}" not found in bodies; skipping scoped apply.`
    );
    return {
      joints: [] as Joint[],
      bodyTree: [] as BodyWithChildren[],
    };
  }
  const scopedBodyTree = ownerWorldBodyUuid
    ? getBodyTree({
        ...sceneState,
        worldBodies: [ownerWorldBodyUuid],
      })
    : fullBodyTree;
  let scopedJoints = orderedAllJoints;
  if (ownerWorldBodyUuid) {
    const rootMap = buildBodyRootMap(
      sceneState.bodies || {},
      sceneState.worldBodies || []
    ) as Record<string, BodyUUID>;
    const jointOwnerBodyMap: Record<string, string> = {};
    Object.values(bodiesMap).forEach((body) => {
      const bodyUuid = body?.uuid;
      if (!bodyUuid || !Array.isArray(body.joints)) return;
      body.joints.forEach((jointRef) => {
        const jointUuid = typeof jointRef === "string" ? jointRef : null;
        if (jointUuid) jointOwnerBodyMap[String(jointUuid)] = bodyUuid;
      });
    });
    scopedJoints = orderedAllJoints.filter((joint) => {
      const ownerBodyUuid = jointOwnerBodyMap[String(joint.uuid)];
      if (!ownerBodyUuid) return false;
      return rootMap[ownerBodyUuid] === ownerWorldBodyUuid;
    });
  }

  return {
    joints: scopedJoints,
    bodyTree: scopedBodyTree,
  };
};

const KEYFRAME_OWNER_SEPARATOR = " :: ";

const resolveOwnerWorldBodyUuidFromKeyframeName = (
  sceneState: ReturnType<typeof getSceneState>,
  keyframeName: string
): string | undefined => {
  if (!keyframeName || !keyframeName.includes(KEYFRAME_OWNER_SEPARATOR)) return undefined;
  const separatorIndex = keyframeName.indexOf(KEYFRAME_OWNER_SEPARATOR);
  if (separatorIndex <= 0) return undefined;
  const ownerLabel = keyframeName.slice(0, separatorIndex).trim();
  if (!ownerLabel) return undefined;

  const worldBodies = Array.isArray(sceneState.worldBodies) ? sceneState.worldBodies : [];
  const candidates = (worldBodies as WorldBodyEntry[]).filter((entry) => {
    const name = typeof entry === "string" ? "" : String(entry?.name || "");
    if (!name) return false;
    // world body names are often auto-numbered (e.g. "Trossen WX250S 1")
    return name === ownerLabel || name.startsWith(`${ownerLabel} `);
  });

  if (candidates.length !== 1) return undefined;
  const hit = candidates[0];
  return typeof hit === "string" ? hit : hit?.uuid;
};

const getOwnerLabelByWorldBodyUuid = (
  sceneState: ReturnType<typeof getSceneState>,
  ownerWorldBodyUuid?: string
): string => {
  if (!ownerWorldBodyUuid) return "Scene";
  const worldBodies = Array.isArray(sceneState.worldBodies) ? sceneState.worldBodies : [];
  const entry = (worldBodies as WorldBodyEntry[]).find((wb) =>
    typeof wb === "string" ? wb === ownerWorldBodyUuid : wb?.uuid === ownerWorldBodyUuid
  );
  if (entry && typeof entry !== "string" && entry.name) return String(entry.name);
  const bodyName = (sceneState.bodies as BodyLookup | undefined)?.[ownerWorldBodyUuid]?.name;
  return bodyName ? String(bodyName) : ownerWorldBodyUuid;
};

export const createKeyframeActions = ({
  set,
  get,
}: {
  set: FileStoreSet;
  get: FileStoreGet;
}): Pick<
  FileStoreActions,
  | "clearAllKeyframes"
  | "mergeKeyframes"
  | "handleKeyframeSelect"
  | "createKeyframe"
  | "deleteKeyframe"
  | "interpolateKeyframes"
> => ({
  clearAllKeyframes: () => {
    if (shouldBlockSceneAuthoringMutation("clearAllKeyframes")) return;
    set({ keyframes: {}, keyframeDetails: {} });
    useSelectionStore.getState().selectKeyframe(null);
  },

  mergeKeyframes: (newKeyframes) => {
    if (shouldBlockSceneAuthoringMutation("mergeKeyframes")) return;
    const { keyframes } = get();
    if (Object.keys(newKeyframes).length === 0) return;
    set({
      keyframes: { ...keyframes, ...newKeyframes },
    });
  },

  handleKeyframeSelect: async (
    keyframeName: string,
    ownerWorldBodyUuid?: string,
    options?: HandleKeyframeSelectOptions
  ): Promise<void> => {
    const preserveWorldRootPose = options?.preserveWorldRootPose !== false;
    const { keyframes } = get();
    const selectionActions = useSelectionStore.getState();
    const sceneActions = getSceneActions();
    const sceneState = getSceneState();
    const effectiveOwnerUuid =
      ownerWorldBodyUuid || resolveOwnerWorldBodyUuidFromKeyframeName(sceneState, keyframeName);
    const { joints } = getScopedBodiesAndJoints(sceneState, effectiveOwnerUuid);

    selectionActions.selectKeyframe(keyframeName);

    const qpos = keyframes[keyframeName];
    if (!qpos || !Array.isArray(qpos)) return;
    const isUnscopedNonDefault = !effectiveOwnerUuid;
    const worldBodyCount = Array.isArray(sceneState.worldBodies)
      ? sceneState.worldBodies.length
      : 0;
    if (isUnscopedNonDefault && worldBodyCount > 1) {
      const structure = analyzeQposStructure(qpos, joints) as QposStructure;
      const mapped = structure.mappingJoints;
      const tailJointCount =
        Array.isArray(mapped) && mapped.length > 0 ? mapped.length : joints.length;
      if (structure.jointValues.length < tailJointCount) {
        logger.warn(
          `[KeyframeActions] Skipping unscoped keyframe "${keyframeName}" in multi-root scene: qpos appears partial (${qpos.length} for ${tailJointCount} tail joints).`
        );
        return;
      }
    }

    const freeJointsInScope = joints.filter((j) => j.kind === "free");
    const hasScalarJoint = joints.some((j) => isScalarJoint(j));
    const freeOnlyObjectKeyframe =
      freeJointsInScope.length === 1 &&
      !hasScalarJoint &&
      joints.length === 1 &&
      (qpos.length === 7 || qpos.length === 8);

    if (freeOnlyObjectKeyframe) {
      const pos = qpos.slice(0, 3).map(Number) as [number, number, number];
      const quat = qpos.slice(3, 7).map(Number) as [number, number, number, number];
      const hostBodyUuid =
        ownerWorldBodyUuid ||
        effectiveOwnerUuid ||
        findBodyForJointUuid(sceneState.bodies || {}, freeJointsInScope[0].uuid);
      if (hostBodyUuid) {
        syncFreeJointRootPose(sceneState, sceneActions, hostBodyUuid, pos, quat);
      }
      return;
    }

    /** MuJoCo qpos: 7 (free) + one value per hinge/slide in model joint order (e.g. book root + cover). */
    const scalarJointsInScope = joints.filter((j) => isScalarJoint(j));
    const freePlusScalarsKeyframe =
      freeJointsInScope.length === 1 &&
      scalarJointsInScope.length > 0 &&
      qpos.length === 7 + scalarJointsInScope.length;

    if (freePlusScalarsKeyframe) {
      const pos = qpos.slice(0, 3).map(Number) as [number, number, number];
      const quat = qpos.slice(3, 7).map(Number) as [number, number, number, number];
      const hostBodyUuid =
        ownerWorldBodyUuid ||
        effectiveOwnerUuid ||
        findBodyForJointUuid(sceneState.bodies || {}, freeJointsInScope[0].uuid);
      if (hostBodyUuid && !preserveWorldRootPose) {
        syncFreeJointRootPose(sceneState, sceneActions, hostBodyUuid, pos, quat);
      }
      const tail = qpos.slice(7);
      scalarJointsInScope.forEach((joint, i) => {
        sceneActions.updateJointValue(joint.uuid, Number(tail[i]) || 0);
      });
      return;
    }

    const structureForApply = analyzeQposStructure(qpos, joints) as QposStructure;
    const keyframeHasFreeBasePlusScalars = structureForApply.baseValues === 7;
    if (keyframeHasFreeBasePlusScalars && !preserveWorldRootPose) {
      const pos = qpos.slice(0, 3).map(Number) as [number, number, number];
      const quat = qpos.slice(3, 7).map(Number) as [number, number, number, number];
      const hostBodyUuid =
        ownerWorldBodyUuid ||
        effectiveOwnerUuid ||
        (freeJointsInScope.length === 1
          ? findBodyForJointUuid(sceneState.bodies || {}, freeJointsInScope[0].uuid)
          : undefined);
      if (hostBodyUuid) {
        syncFreeJointRootPose(sceneState, sceneActions, hostBodyUuid, pos, quat);
      }
    }

    applyKeyframe(
      qpos,
      joints,
      [],
      sceneActions.updateJointValue,
      sceneActions.updateBallJointQuat
    ) as KeyframeApplyResult;
  },

  createKeyframe: async (keyframeName: string, ownerWorldBodyUuid?: string): Promise<void> => {
    if (shouldBlockSceneAuthoringMutation("createKeyframe")) return;
    const sceneState = getSceneState();
    const { joints } = getScopedBodiesAndJoints(sceneState, ownerWorldBodyUuid);
    const currentJointValues = getJointValuesFromMap(sceneState.joints || {});
    const qpos = joints.flatMap((joint) =>
      getJointQposValues(sceneState, joint, currentJointValues)
    );

    const { keyframes } = get();
    const ownerLabel = getOwnerLabelByWorldBodyUuid(sceneState, ownerWorldBodyUuid);
    const scopedBaseName = `${ownerLabel}${KEYFRAME_OWNER_SEPARATOR}${keyframeName}`;
    let scopedName = scopedBaseName;
    let suffix = 2;
    while (keyframes[scopedName]) {
      scopedName = `${scopedBaseName} (${suffix})`;
      suffix += 1;
    }
    set({ keyframes: { ...keyframes, [scopedName]: qpos } });

    const selectionActions = useSelectionStore.getState();
    selectionActions.selectKeyframe(scopedName);
  },

  deleteKeyframe: async (keyframeName: string): Promise<void> => {
    if (shouldBlockSceneAuthoringMutation("deleteKeyframe")) return;
    const { keyframes } = get();
    const selectionActions = useSelectionStore.getState();

    const newKeyframes = { ...keyframes };
    delete newKeyframes[keyframeName];
    set({ keyframes: newKeyframes });

    if (selectionActions.selectedKeyframe === keyframeName) {
      selectionActions.selectKeyframe(null);
    }
  },

  interpolateKeyframes: async (
    keyframeName1: string,
    keyframeName2: string,
    factor: number,
    ownerWorldBodyUuid?: string
  ): Promise<void> => {
    const { keyframes } = get();
    const sceneActions = getSceneActions();
    const sceneState = getSceneState();
    const inferredOwnerFromKf1 = resolveOwnerWorldBodyUuidFromKeyframeName(
      sceneState,
      keyframeName1
    );
    const inferredOwnerFromKf2 = resolveOwnerWorldBodyUuidFromKeyframeName(
      sceneState,
      keyframeName2
    );
    const effectiveOwnerUuid = ownerWorldBodyUuid || inferredOwnerFromKf1 || inferredOwnerFromKf2;
    const { joints, bodyTree } = getScopedBodiesAndJoints(sceneState, effectiveOwnerUuid);

    let qpos1: number[] | undefined;
    const qpos2 = keyframes[keyframeName2];

    if (keyframeName1 === "current") {
      const currentJointValues = getJointValuesFromMap(sceneState.joints);
      qpos1 = joints.map((joint) => currentJointValues[joint.name] ?? 0);
    } else {
      qpos1 = keyframes[keyframeName1];
    }

    if (!qpos1 || !qpos2) return;

    const structure1 = analyzeQposStructure(qpos1, joints) as QposStructure;
    const structure2 = analyzeQposStructure(qpos2, joints) as QposStructure;

    if (structure1.baseValues !== structure2.baseValues) {
      const interpolatedJointValues = structure1.jointValues.map((value1, index) => {
        const value2 = structure2.jointValues[index] ?? 0;
        return value1 + (value2 - value1) * factor;
      });

      joints.forEach((joint, index) => {
        if (index < interpolatedJointValues.length) {
          sceneActions.updateJointValue(joint.uuid, interpolatedJointValues[index]);
        }
      });
      return;
    }

    const getTransformedJointValues = (qpos: number[]): number[] => {
      const tempResult: { success: boolean; appliedValues: Record<string, number> } = {
        success: false,
        appliedValues: {},
      };
      const result = applyKeyframe(
        qpos,
        joints,
        bodyTree as unknown as Body[],
        (jointName: string, value: number) => {
          tempResult.appliedValues[jointName] = value;
        }
      ) as KeyframeApplyResult;
      if (result.success) {
        return joints.map((joint) => tempResult.appliedValues[joint.name] ?? 0);
      }
      const structure = analyzeQposStructure(qpos, joints) as QposStructure;
      return structure.jointValues;
    };

    const transformedJointValues1 = getTransformedJointValues(qpos1);
    const transformedJointValues2 = getTransformedJointValues(qpos2);

    const interpolatedJointValues = transformedJointValues1.map((value1, index) => {
      const value2 = transformedJointValues2[index] ?? 0;
      return value1 + (value2 - value1) * factor;
    });

    joints.forEach((joint, index) => {
      if (index < interpolatedJointValues.length) {
        sceneActions.updateJointValue(joint.uuid, interpolatedJointValues[index]);
      }
    });
  },
});
